import { theme } from "@common/theme/theme"

/**
 * Shared vocabulary for structured-correction points (socle P-1810 contract).
 *
 * Used by the analysis cards (StructuredCorrectionBlock) and the interactive
 * markers drawn over the student's copy (CorrectionMarkersOverlay) so both
 * surfaces stay color- and label-consistent.
 */

// Type vocabulary is taken as-is from the socle. Markers/chips are colored
// per type — red / yellow / green.
export const TYPE_ORDER = ["erreur", "vigilance", "réussite"]

export const TYPE_CONFIG = {
    erreur: {
        label: "Erreur",
        color: theme.colors.error,
        light: theme.colors.errorLight,
    },
    vigilance: {
        label: "Vigilance",
        color: theme.colors.warning,
        light: theme.colors.warningLight,
    },
    réussite: {
        label: "Réussite",
        color: theme.colors.success,
        light: theme.colors.successLight,
    },
}

// When the AI notion couldn't be grounded on a known skill part, we show a
// neutral label — never an invented notion name.
export const UNKNOWN_NOTION_LABEL = "Notion à préciser"

// Defensive fallback so an unexpected type is still displayed (never dropped),
// without inventing a colored vocabulary for it.
export const typeConfig = (type) =>
    TYPE_CONFIG[type] ?? {
        label: type ?? "Point",
        color: theme.colors.grey,
        light: theme.colors.lightMore,
    }

/**
 * True when every displayable coordinate of the anchor is present — the
 * client-side markers need a full normalized bbox to be positioned.
 */
export const hasUsableAnchor = (point) => {
    const a = point?.anchor
    return (
        !!a &&
        [a.x, a.y, a.w, a.h].every((v) => typeof v === "number" && !isNaN(v))
    )
}

/**
 * Resolve the work-image page a point belongs to. Mirrors the backend
 * renderer: a page index out of range is clamped to the last page (a model
 * that over-counts pages must not drop a marker).
 */
export const anchorPageIndex = (point, pagesCount) => {
    const raw = point?.anchor?.page ?? 0
    if (!pagesCount || pagesCount < 1) return 0
    return Math.min(Math.max(0, raw), pagesCount - 1)
}
